import { Callout } from '@studio/design-system/callout';
import type { ControllerStopResult } from '#~/ipjoystick/comm.js';
import { Button } from '@studio/design-system/button';
import * as Dialog from '@studio/design-system/dialog';
import { Group } from '@studio/design-system/form';
import { Input } from '@studio/design-system/input';
import { useState } from 'react';
import { forwardRef } from 'react';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { Sel, STagNew } from '#~/stag/index.js';

export const AdvancedSettingsModal = forwardRef<
  Dialog.Api,
  {
    name: string;
    ip: string;
    port: number;
    onDelete: (showResult: (result: ControllerStopResult) => void) => void;
    onSave: (name: string) => void;
  }
>(({ name, ip, port, onDelete, onSave }, forwardedRef) => {
  const [value, setValue] = useState(name);
  // The dialog is portaled with captured props; keep settled guidance in its
  // own mounted state so an uncertain delete remains visible above the overlay.
  const [stopNotice, setStopNotice] = useState<ControllerStopResult | null>(null);

  return (
    <Dialog.Root ref={forwardedRef}>
      <Dialog.Portal>
        <Dialog.Overlay>
          <Dialog.Panel>
            <div className={'flex flex-col gap-28'}>
              <Dialog.Header title='Advanced Settings' closeable />
              <Dialog.Content className='flex w-[550px] flex-col gap-24 px-40'>
                <ControllerStopNotice result={stopNotice} />
                <Group label='Controller Name' className='pointer-events-auto'>
                  <Input disabled={false} value={value} onChange={setValue} />
                </Group>
                <Group label={`${vendorName()} IP Address`}>
                  <Input disabled value={ip} />
                </Group>
                <Group label={`${vendorName()} Port Number`}>
                  <Input type='number' disabled value={port} />
                </Group>
              </Dialog.Content>
              <Dialog.Footer>
                <Button
                  size='lg'
                  variant='danger'
                  onClick={() => onDelete(setStopNotice)}
                  {...STagNew(Sel.AccountSettings, 'ip_controller_delete')}
                >
                  Delete Controller
                </Button>
                <div className='flex gap-12'>
                  <Dialog.Close variant='secondary'>cancel</Dialog.Close>
                  <Button
                    size='lg'
                    onClick={() => {
                      onSave(value);
                    }}
                  >
                    Save Changes
                  </Button>
                </div>
              </Dialog.Footer>
            </div>
          </Dialog.Panel>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

// Share the same outcome facts between the active dialog and the retained list.
export const ControllerStopNotice = ({ result }: { result: ControllerStopResult | null }) => (
  <>
    {result?.outcomeUnknown && (
      <Callout className='mb-16' severity='warning' title='Delete could not be confirmed'>
        <Callout.P>
          {`The ${vendorName()} application did not confirm whether the controller process stopped or its saved configuration was removed. Input from this controller is blocked locally. Try Delete again.`}
        </Callout.P>
      </Callout>
    )}
    {result?.physicalStopUnconfirmed && (
      <Callout className='mb-16' severity='warning' title='Physical stop could not be confirmed'>
        <Callout.P>
          {result.processStopped
            ? 'Local motion commands stopped. A physical halt was not confirmed.'
            : 'The controller may still be running. Local motion commands stopped. A physical halt was not confirmed. Try Delete again.'}
        </Callout.P>
      </Callout>
    )}
    {result && result.hadRuntime && !result.processStopped && !result.physicalStopUnconfirmed && (
      <Callout className='mb-16' severity='warning' title='Controller process did not exit'>
        <Callout.P>
          The controller process did not confirm exit. It may still be running. Try Delete again.
        </Callout.P>
      </Callout>
    )}
  </>
);
